import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PlanGeneratorInput, PlanGeneratorWizardState, PlanGenerationResult } from '../../types/athlete';
import { getSessionTemplates } from '../../lib/training/sessionSelector';
import { generateTrainingPlan } from '../../lib/training/planGenerator';
import StepRaceDetails from './StepRaceDetails';
import StepIdealWeek from './StepIdealWeek';
import StepExperience from './StepExperience';
import StepPreview from './StepPreview';
import { ArrowLeft, ArrowRight, Check, Sparkles, Zap } from 'lucide-react';

const PlanGeneratorWizard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewResult, setPreviewResult] = useState<PlanGenerationResult | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [useOxygenAddict, setUseOxygenAddict] = useState(true); // Default to Oxygen Addict

  const [state, setState] = useState<PlanGeneratorWizardState>({
    step: 1,
    raceDetails: {
      raceType: null,
      raceDate: null,
      name: '',
      description: '',
    },
    idealWeekTemplate: {
      days: {
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
        6: [],
        7: [],
      },
    },
    sessionTemplates: getSessionTemplates(),
    experienceLevel: null,
    generatedPreview: null,
  });

  useEffect(() => {
    if (useOxygenAddict) {
      applyOxygenAddictStarter(state.sessionTemplates);
    }
  }, [useOxygenAddict, state.sessionTemplates]);

  const handleNext = async () => {
    if (state.step === 3) {
      await generatePreview();
    } else if (state.step < 4) {
      setState(prev => ({ ...prev, step: (prev.step + 1) as 1 | 2 | 3 | 4 }));
    }
  };

  const applyOxygenAddictStarter = (templates: PlanGeneratorWizardState['sessionTemplates']) => {
    const findTemplate = (discipline: string, category: string) =>
      templates.find(template => template.discipline === discipline && template.category === category) ||
      templates.find(template => template.discipline === discipline) ||
      templates[0];

    const templateId = (discipline: string, category: string) => findTemplate(discipline, category)?.id || '';

    const createSession = (templateIdValue: string, orderTime: string, duration?: number) => ({
      id: `ideal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      templateId: templateIdValue,
      orderTime,
      durationOverride: duration,
    });

    const updatedWeek = {
      days: {
        1: [],
        2: [
          createSession(templateId('bike', 'interval'), '6AM', 60),
        ],
        3: [
          createSession(templateId('run', 'tempo'), '6AM', 45),
          createSession(templateId('swim', 'endurance'), '6PM', 40),
        ],
        4: [
          createSession(templateId('bike', 'brick'), '6AM', 75),
        ],
        5: [
          createSession(templateId('swim', 'endurance'), '6AM', 40),
        ],
        6: [
          createSession(templateId('bike', 'endurance'), '7AM', 120),
        ],
        7: [
          createSession(templateId('run', 'endurance'), '7AM', 60),
        ],
      },
    };

    setState(prev => ({
      ...prev,
      idealWeekTemplate: updatedWeek,
    }));
  };

  const handleBack = () => {
    if (state.step > 1) {
      setState(prev => ({ ...prev, step: (prev.step - 1) as 1 | 2 | 3 | 4 }));
    }
  };

  const generatePreview = async () => {
    if (!state.raceDetails.raceType || !state.raceDetails.raceDate || !state.experienceLevel) {
      return;
    }

    setGenerating(true);
    setGenerationError(null);

    try {
      const athleteSettings = {
        id: '',
        css_value: null,
        cycling_ftp: null,
        run_reference_distance: 10,
        run_reference_time: '55:00',
        run_additional_distances: null,
        max_hr: null,
        resting_hr: null,
        hr_zone_method: null,
        created_at: null,
        updated_at: null,
      };

      const totalSessions = Object.values(state.idealWeekTemplate.days).reduce(
        (sum, day) => sum + day.length,
        0
      );

      const input: PlanGeneratorInput = {
        raceType: state.raceDetails.raceType,
        raceDate: state.raceDetails.raceDate,
        sessionsPerWeek: totalSessions,
        preferredDays: Object.keys(state.idealWeekTemplate.days).map(Number),
        maxWeekdayDuration: 90,
        maxWeekendDuration: 180,
        experienceLevel: state.experienceLevel,
        athleteSettingsId: '',
        idealWeekTemplate: state.idealWeekTemplate,
        sessionTemplates: state.sessionTemplates,
      };

      const result = await generateTrainingPlan(input, athleteSettings);

      setPreviewResult(result);
      setState(prev => ({
        ...prev,
        step: 4,
        generatedPreview: result,
      }));
    } catch (error) {
      console.error('Error generating plan:', error);
      setGenerationError('Failed to generate plan. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!user || !previewResult) {
      return;
    }

    setLoading(true);
    try {
      navigate('/plans');
    } catch (error) {
      console.error('Error saving plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = (): boolean => {
    switch (state.step) {
      case 1:
        return !!state.raceDetails.raceType && !!state.raceDetails.raceDate;
      case 2:
        return Object.values(state.idealWeekTemplate.days).some(day => day.length > 0);
      case 3:
        return !!state.experienceLevel;
      case 4:
        return !!previewResult;
      default:
        return false;
    }
  };

  const stepTitles = [
    { step: 1, title: 'Race Details', description: 'Tell us about your race' },
    { step: 2, title: 'Ideal Week', description: 'Design your perfect week' },
    { step: 3, title: 'Experience', description: 'Your training background' },
    { step: 4, title: 'Preview', description: 'Review your plan' },
  ];

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-[rgba(168,85,247,0.2)] rounded-lg">
            <Sparkles className="w-6 h-6 text-[#a855f7]" />
          </div>
          <h1 className="text-2xl font-bold">AI Plan Generator</h1>
        </div>
        <p className="text-muted">
          Get a personalized, periodized training plan powered by AI
        </p>
      </div>

      <div className="flex items-center justify-between mb-8">
        {stepTitles.map((item, index) => (
          <React.Fragment key={item.step}>
            <div className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  state.step >= item.step
                    ? 'bg-[#a855f7] text-white'
                    : 'bg-[#141921] text-[#718096]'
                }`}
              >
                {state.step > item.step ? <Check size={20} /> : item.step}
              </div>
              <div className="ml-3 hidden md:block">
                <p className="font-medium text-sm">{item.title}</p>
                <p className="text-xs text-muted">{item.description}</p>
              </div>
            </div>
            {index < stepTitles.length - 1 && (
              <div
                className={`flex-1 h-1 mx-4 ${
                  state.step > item.step ? 'bg-[#a855f7]' : 'bg-[#141921]'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Training Methodology Selector */}
      {state.step === 1 && (
        <div className="card-base p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Training Methodology</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Oxygen Addict Option */}
              <button
                onClick={() => {
                  setUseOxygenAddict(true);
                  applyOxygenAddictStarter(state.sessionTemplates);
                }}

              className={`p-6 rounded-lg border-2 transition-all text-left ${
                useOxygenAddict
                  ? 'border-[#3b82f6] bg-[#3b82f6]/10'
                  : 'border-[#2d3748] bg-[#1a1f2e] hover:border-[#3b82f6]/50'
              }`}
            >
              <div className="flex items-start space-x-3 mb-3">
                <Zap className={`${useOxygenAddict ? 'text-[#3b82f6]' : 'text-muted'}`} size={24} />
                <div>
                  <h4 className="font-semibold text-white mb-1">Oxygen Addict T.I.M.E.</h4>
                  <p className="text-sm text-muted">Time-efficient Intelligent Multi-sport Evolution</p>
                </div>
              </div>
              <ul className="text-sm text-muted space-y-1">
                <li>• 7-8 hours/week total training</li>
                <li>• 2-2-2 pattern: 2 swims, 2 bikes, 2 runs</li>
                <li>• Monday always recovery (most important day)</li>
                <li>• Optimized for busy professionals</li>
              </ul>
            </button>

            {/* Standard Option */}
            <button
              onClick={() => {
                setUseOxygenAddict(false);
              }}
              className={`p-6 rounded-lg border-2 transition-all text-left ${
                !useOxygenAddict
                  ? 'border-[#3b82f6] bg-[#3b82f6]/10'
                  : 'border-[#2d3748] bg-[#1a1f2e] hover:border-[#3b82f6]/50'
              }`}
            >
              <div className="flex items-start space-x-3 mb-3">
                <Sparkles className={`${!useOxygenAddict ? 'text-[#a855f7]' : 'text-muted'}`} size={24} />
                <div>
                  <h4 className="font-semibold text-white mb-1">Standard AI Plan</h4>
                  <p className="text-sm text-muted">Flexible, customizable training</p>
                </div>
              </div>
              <ul className="text-sm text-muted space-y-1">
                <li>• Customizable sessions per week</li>
                <li>• Flexible training days</li>
                <li>• Adjustable durations</li>
                <li>• Traditional periodization</li>
              </ul>
            </button>
          </div>
        </div>
      )}

      <div className="card-base p-8 mb-8">
        {state.step === 1 && (
          <StepRaceDetails
            raceDetails={state.raceDetails}
            onUpdate={(updates) =>
              setState(prev => ({
                ...prev,
                raceDetails: { ...prev.raceDetails, ...updates },
              }))
            }
          />
        )}

        {state.step === 2 && (
          <StepIdealWeek
            idealWeekTemplate={state.idealWeekTemplate}
            sessionTemplates={state.sessionTemplates}
            onUpdateIdealWeek={(template: PlanGeneratorWizardState['idealWeekTemplate']) =>
              setState(prev => ({
                ...prev,
                idealWeekTemplate: template,
              }))
            }
            onUpdateTemplates={(templates: PlanGeneratorWizardState['sessionTemplates']) =>
              setState(prev => ({
                ...prev,
                sessionTemplates: templates,
              }))
            }
          />
        )}

        {state.step === 3 && (
          <StepExperience
            experienceLevel={state.experienceLevel}
            onUpdate={(level) =>
              setState(prev => ({ ...prev, experienceLevel: level }))
            }
          />
        )}

        {state.step === 4 && previewResult && (
          <StepPreview
            result={previewResult}
            raceDetails={state.raceDetails}
            idealWeekTemplate={state.idealWeekTemplate}
            onRegenerate={generatePreview}
          />
        )}

        {generationError && (
          <div className="mt-4 p-4 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.2)] rounded-lg">
            <p className="text-red-400">{generationError}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={state.step === 1 || generating}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            state.step === 1 || generating
              ? 'opacity-0 pointer-events-none'
              : 'bg-[#141921] text-[#e2e8f0] hover:bg-[#2d3748]'
          }`}
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        {state.step < 4 ? (
          <button
            onClick={handleNext}
            disabled={!canProceed() || generating}
            className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors ${
              canProceed() && !generating
                ? 'bg-[#a855f7] text-white hover:bg-[#c084fc]'
                : 'bg-[#141921] text-[#718096] cursor-not-allowed'
            }`}
          >
            {generating ? (
              <>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <span>{state.step === 3 ? 'Generate Plan' : 'Next'}</span>
                <ArrowRight size={18} />
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-2 bg-[#a855f7] text-white rounded-lg hover:bg-[#c084fc] transition-colors disabled:opacity-50"
          >
            <span>{loading ? 'Saving...' : 'Save Plan'}</span>
            <Check size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default PlanGeneratorWizard;
